import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Box,
  Button,
  useMediaQuery,
  useTheme,
  Paper,
  Typography,
  IconButton,
} from "@mui/material";
import { createMTC, updateMTC } from "../../../Services/Services";
import { useNavigate, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const MaterialTestCertificateForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();

  // Get mode and data from navigation state
  const mode = location.state?.mode || "create";
  const editData = location.state?.data || null;

    const getSelectedDivision = () => {
    try {
      const stored = localStorage.getItem("selectedDivision");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error("Failed to parse selectedDivision:", e);
      return null;
    }
  };

  const storedDivision = getSelectedDivision();

  // Initial empty form data
  const getEmptyForm = () => ({
    size: "",
    material: "",
    tc_no: "",
    heat_no: "",
    tube_qty: "",
    uom: "",
    per_tube_length: "",
    qty: "",
    year: new Date().getFullYear().toString(),
    file: null,
    fileName: "",
    fileUrl: "",
    division: storedDivision?.division_id || ""
  });

  const [formData, setFormData] = useState(getEmptyForm());
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(mode === "edit");

  // Populate form if in edit mode
  useEffect(() => {
    if (mode === "edit" && editData) {
      console.log("Edit Data:", editData); // Debug log
      const divisionData = getSelectedDivision();
      setFormData({
        size: editData.size || "",
        material: editData.material || "",
        tc_no: editData.tc_no || "",
        heat_no: editData.heat_no || "",
        tube_qty: editData.tube_qty || "",
        uom: editData.uom || "",
        per_tube_length: editData.per_tube_length || "",
        qty: editData.qty || "",
        year: editData.year || new Date().getFullYear().toString(),
        file: editData.file_url || editData.file || null, // Store URL as file for existing files
        fileName: editData.file_name || editData.filename || "",
        fileUrl: editData.file_url || editData.file || "",
        division: divisionData?.division_id || editData.division || "",
      });
      setIsEditMode(true);
    } else {
      setIsEditMode(false);
    }
  }, [mode, editData]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size should be less than 5MB");
        return;
      }

      // Create object URL for preview
      const fileUrl = URL.createObjectURL(file);

      setFormData((prev) => ({
        ...prev,
        file: file, // Store actual File object
        fileName: file.name, // Store file name
        fileUrl: fileUrl, // Store object URL for preview
      }));

      toast.success(`File "${file.name}" selected successfully!`);
    }
  };

  const handleRemoveFile = () => {
    // Clean up object URL if it exists
    if (formData.fileUrl && formData.fileUrl.startsWith("blob:")) {
      URL.revokeObjectURL(formData.fileUrl);
    }

    setFormData((prev) => ({
      ...prev,
      file: null,
      fileName: "",
      fileUrl: "",
    }));

    // Reset file input
    const fileInput = document.getElementById("file-input");
    if (fileInput) fileInput.value = "";
  };

  const handleViewFile = () => {
    if (formData.fileUrl) {
      window.open(formData.fileUrl, "_blank", "noopener,noreferrer");
    } else if (formData.file) {
      const fileUrl = URL.createObjectURL(formData.file);
      window.open(fileUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(fileUrl), 100);
    } else {
      toast.info("No file available to view");
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.size?.trim()) newErrors.size = "Size is required";
    if (!formData.material?.trim()) newErrors.material = "Material is required";
    if (!formData.tc_no?.trim()) newErrors.tc_no = "TC No. is required";
    if (!formData.heat_no?.trim()) newErrors.heat_no = "Heat No. is required";
    if (!formData.tube_qty) newErrors.tube_qty = "Tube Qty is required";
    if (!formData.uom?.trim()) newErrors.uom = "UOM is required";
    if (!formData.per_tube_length)
      newErrors.per_tube_length = "Per Tube Length is required";
    if (!formData.qty) newErrors.qty = "Qty is required";
    if (!formData.year) newErrors.year = "Year is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    // Clean up object URL if it exists
    if (formData.fileUrl && formData.fileUrl.startsWith("blob:")) {
      URL.revokeObjectURL(formData.fileUrl);
    }
    setFormData(getEmptyForm());
    setErrors({});
    // Reset file input
    const fileInput = document.getElementById("file-input");
    if (fileInput) fileInput.value = "";
  };

  const handleSubmit = async () => {
    const divisionData = getSelectedDivision();
    if (!divisionData?.division_id) {
      toast.error("No division selected. Please select a division from the header.");
      return;
    }
    if (!validateForm()) {
      toast.error("Please fill all required fields");
      return;
    }

    setLoading(true);

    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append("size", formData.size);
      formDataToSend.append("material", formData.material);
      formDataToSend.append("tc_no", formData.tc_no);
      formDataToSend.append("heat_no", formData.heat_no);
      formDataToSend.append("tube_qty", formData.tube_qty);
      formDataToSend.append("uom", formData.uom);
      formDataToSend.append("per_tube_length", formData.per_tube_length);
      formDataToSend.append("qty", formData.qty);
      formDataToSend.append("year", formData.year);
      formDataToSend.append("division", divisionData.division_id);

      // Only append file if it's a File object (newly selected file)
      if (formData.file instanceof File) {
        formDataToSend.append("file", formData.file);
      }

      // If in edit mode, include the ID
      if (isEditMode && editData?.id) {
        formDataToSend.append("id", editData.id);
      }

      let response;
      if (isEditMode && editData?.id) {
        // Update existing record
        response = await updateMTC(editData.id, formDataToSend);
        toast.success("MTC updated successfully!");
      } else {
        // Create new record
        response = await createMTC(formDataToSend);
        toast.success("MTC created successfully!");

        // Reset form after successful creation
        resetForm();
      }
    } catch (err) {
      console.error("Error submitting MTC:", err);
      toast.error(
        err.response?.data?.message || "Failed to save MTC. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isEditMode) {
      // In edit mode, reset to original data
      if (editData) {
        // Clean up any blob URLs
        if (formData.fileUrl && formData.fileUrl.startsWith("blob:")) {
          URL.revokeObjectURL(formData.fileUrl);
        }

         const divisionData = getSelectedDivision(); 

        setFormData({
          size: editData.size || "",
          material: editData.material || "",
          tc_no: editData.tc_no || "",
          heat_no: editData.heat_no || "",
          tube_qty: editData.tube_qty || "",
          uom: editData.uom || "",
          per_tube_length: editData.per_tube_length || "",
          qty: editData.qty || "",
          year: editData.year || new Date().getFullYear().toString(),
          file: editData.file_url || editData.file || null,
          fileName: editData.file_name || editData.filename || "",
          fileUrl: editData.file_url || editData.file || "",
          division: divisionData?.division_id || editData.division || "",
        });
      }
    } else {
      // In create mode, reset to empty
      resetForm();
    }
    setErrors({});
  };

// Auto-calculate qty = tube_qty * per_tube_length
useEffect(() => {
  const tubeQty = parseFloat(formData.tube_qty) || 0;
  const perTubeLength = parseFloat(formData.per_tube_length) || 0;
  
  const calculatedQty = tubeQty * perTubeLength;
  setFormData((prev) => ({ ...prev, qty: calculatedQty ? calculatedQty.toString() : "" }));
}, [formData.tube_qty, formData.per_tube_length]);

  const handleBack = () => {
    // Clean up any blob URLs before navigating away
    if (formData.fileUrl && formData.fileUrl.startsWith("blob:")) {
      URL.revokeObjectURL(formData.fileUrl);
    }
    navigate("/dashboard/mtc-list");
  };

  // Check if file exists
  const hasFile = !!(formData.file || formData.fileName || formData.fileUrl);

  return (
    <Box p={isMobile ? 1 : 3}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: isMobile ? "wrap" : "nowrap",
          }}
        >
          <Button
            variant="outlined"
            onClick={handleBack}
            sx={{ borderRadius: 0, textTransform: "none" }}
          >
            Back to List
          </Button>
          <Button
            variant="outlined"
            onClick={handleCancel}
            sx={{ borderRadius: 0, textTransform: "none" }}
          >
            {isEditMode ? "Revert Changes" : "Reset"}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{ borderRadius: 0, textTransform: "none", minWidth: 100 }}
          >
            {loading ? "Saving..." : isEditMode ? "Update" : "Submit"}
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3, backgroundColor: "#fff" }}>
        {/* Form Grid - 3 columns on desktop, 1 on mobile */}
        <Grid container spacing={3}>
          {/* Row 1: Size & Material & Year */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Size *"
              value={formData.size}
              onChange={(e) => handleChange("size", e.target.value)}
              error={!!errors.size}
              helperText={errors.size}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Material *"
              value={formData.material}
              onChange={(e) => handleChange("material", e.target.value)}
              error={!!errors.material}
              helperText={errors.material}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Year *"
              type="number"
              value={formData.year}
              onChange={(e) => handleChange("year", e.target.value)}
              error={!!errors.year}
              helperText={errors.year}
              size="small"
              InputProps={{ inputProps: { min: 2000, max: 2100 } }}
            />
          </Grid>

          {/* Row 2: TC No & Heat No */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="TC No. *"
              value={formData.tc_no}
              onChange={(e) => handleChange("tc_no", e.target.value)}
              error={!!errors.tc_no}
              helperText={errors.tc_no}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Heat No. *"
              value={formData.heat_no}
              onChange={(e) => handleChange("heat_no", e.target.value)}
              error={!!errors.heat_no}
              helperText={errors.heat_no}
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="UOM *"
              value={formData.uom}
              onChange={(e) => handleChange("uom", e.target.value)}
              error={!!errors.uom}
              helperText={errors.uom}
              size="small"
            />
          </Grid>

          {/* Row 3: Tube Qty & UOM */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Tube Qty (NOS) *"
              type="number"
              value={formData.tube_qty}
              onChange={(e) => handleChange("tube_qty", e.target.value)}
              error={!!errors.tube_qty}
              helperText={errors.tube_qty}
              size="small"
            />
          </Grid>

          {/* Row 4: Per Tube Length & Qty */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Per Tube Length *"
              type="number"
              value={formData.per_tube_length}
              onChange={(e) => handleChange("per_tube_length", e.target.value)}
              error={!!errors.per_tube_length}
              helperText={errors.per_tube_length}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Qty *"
              type="number"
              value={formData.qty}
              onChange={(e) => handleChange("qty", e.target.value)}
              error={!!errors.qty}
              helperText={errors.qty}
              size="small"
            />
          </Grid>

          {/* Row 5: File Upload with View Option */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              {/* Upload Button */}
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                sx={{ textTransform: "none", height: 40 }}
              >
                {formData.fileName ? "Change File" : "Upload File"}
                <input
                  id="file-input"
                  type="file"
                  hidden
                  onChange={handleFileChange}
                  accept="*/*"
                />
              </Button>

              {/* Show file info and actions when file exists */}
              {hasFile && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    {formData.fileName || "File selected"}
                  </Typography>

                  {/* View File Button */}
                  <IconButton
                    size="small"
                    onClick={handleViewFile}
                    color="primary"
                    title="View file"
                    sx={{
                      border: "1px solid #ccc",
                      borderRadius: 1,
                      padding: "4px",
                    }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>

                  {/* Remove File Button */}
                  <IconButton
                    size="small"
                    onClick={handleRemoveFile}
                    color="error"
                    title="Remove file"
                    sx={{
                      border: "1px solid #ccc",
                      borderRadius: 1,
                      padding: "4px",
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>

            {/* File Size Limit Info */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              Max file size: 5MB | Supported formats: All files
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default MaterialTestCertificateForm;
